import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../components/AuthProvider';
import { supabase } from '../../lib/auth';
import { getDayAppointments, contractTypeOf, type DayRdv } from '../../lib/refonteApi';
import { type LifecycleStatus } from '../../lib/appointmentLifecycle';
import RdvTabs from '../../components/refonte/RdvTabs';
import StatusBadge from '../../components/refonte/StatusBadge';
import ContractBadge from '../../components/refonte/ContractBadge';
import RdvDetail from '../../components/refonte/RdvDetail';
import { SkeletonRows } from '../../components/refonte/Skeleton';
import EmptyState from '../../components/refonte/EmptyState';

const toISODate = (d: Date) => d.toISOString().split('T')[0];
const fmtDay = (d: Date) => d.toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'long' });

const Stat: React.FC<{ value: React.ReactNode; label: string; tone?: string }> = ({ value, label, tone }) => (
  <div className="text-center px-3 first:pl-0">
    <div className={`font-mono text-[18px] font-bold leading-none ${tone || ''}`}>{value}</div>
    <div className="font-mono text-[9px] uppercase tracking-widest text-faint mt-1">{label}</div>
  </div>
);

// Page RDV À CONFIRMER (1ère tâche SDR du matin) — issue du découpage de Day to Day.
const RdvConfirmerPage: React.FC = () => {
  const { profile } = useAuth();
  const role = profile?.role || 'admin';
  const [date, setDate] = useState<Date>(new Date());
  const [rows, setRows] = useState<DayRdv[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [selId, setSelId] = useState<string | null>(null);
  const [sdrFilter, setSdrFilter] = useState<string>(''); // filtre par SDR (retour Quentin 25/09)
  const dateStr = toISODate(date);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try { setRows(await getDayAppointments(dateStr)); }
    catch (e: any) { setErr(e.message); }
    setLoading(false);
  }, [dateStr]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const ch = supabase.channel(`rdvconf_${dateStr}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'appointments', filter: `date=eq.${dateStr}` }, () => load())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [dateStr, load]);

  useEffect(() => { if (rows.length && !rows.some(r => r.id === selId)) setSelId(rows[0].id); }, [rows, selId]);

  const shiftDay = (delta: number) => {
    const d = new Date(date); d.setDate(d.getDate() + delta);
    if (d.getDay() === 0) d.setDate(d.getDate() + (delta > 0 ? 1 : -2));
    if (d.getDay() === 6) d.setDate(d.getDate() + (delta > 0 ? 2 : -1));
    setDate(d); setSelId(null);
  };

  const countBy = (s: LifecycleStatus) => rows.filter(r => r.lifecycle_status === s).length;
  const pendingConfirm = rows.filter(r => r.lifecycle_status == null || r.lifecycle_status === 'posed').length;
  const nowTs = Date.now();
  const isPast = (r: DayRdv) => !!r.date && new Date(`${r.date}T${(r.time || '23:59').slice(0, 5)}`).getTime() < nowTs;
  const isPending = (r: DayRdv) => r.lifecycle_status == null || r.lifecycle_status === 'posed' || r.lifecycle_status === 'confirmed';
  const toFollowUp = rows.filter(r => isPending(r) && isPast(r));
  const sel = rows.find(r => r.id === selId) || null;
  // Filtre par SDR : liste des SDR présents ce jour + application.
  const sdrNames = [...new Set(rows.map(r => r.sdr?.sales_name).filter(Boolean) as string[])].sort((a, b) => a.localeCompare(b));
  const shownRows = sdrFilter ? rows.filter(r => (r.sdr?.sales_name || '') === sdrFilter) : rows;

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
        <h1 className="text-[21px] font-bold tracking-tight">RDV à confirmer</h1>
      <RdvTabs />
        <div className="flex items-center gap-2 flex-wrap">
          {sdrNames.length > 1 && (
            <select value={sdrFilter} onChange={e => setSdrFilter(e.target.value)}
              className="rounded-lg border border-line-2 bg-panel px-2.5 h-8 text-[12.5px]" title="Filtrer par SDR">
              <option value="">Tous les SDR</option>
              {sdrNames.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          )}
          <button onClick={() => shiftDay(-1)} aria-label="Jour précédent" className="w-8 h-8 rounded-lg border border-line-2 hover:border-ink">‹</button>
          <span className="font-mono text-[12px] text-dim capitalize min-w-[180px] text-center">{fmtDay(date)}</span>
          <button onClick={() => shiftDay(1)} aria-label="Jour suivant" className="w-8 h-8 rounded-lg border border-line-2 hover:border-ink">›</button>
        </div>
      </div>
      <p className="text-dim text-[13px] mb-5">Les RDV du jour à confirmer avec le prospect — 1ère tâche du matin · temps réel {loading && '· maj…'}</p>

      {err && <div className="mb-4 text-[13px] text-st-bad bg-st-bad/10 border border-st-bad/20 rounded-lg px-3 py-2 font-mono">{err}</div>}

      {toFollowUp.length > 0 && (
        <button onClick={() => setSelId(toFollowUp[0].id)}
          className="mb-4 w-full flex items-center gap-2 rounded-lg bg-st-warn/10 border border-st-warn/25 text-st-warn px-3 py-2 text-[13px] font-semibold text-left hover:bg-st-warn/15 transition">
          ⏰ {toFollowUp.length} RDV dont l'heure est passée sans résultat — à statuer (effectué / no-show). En cas de doute, demande au client.
        </button>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-4 lg:h-[78vh] items-stretch">
        <div className="bg-panel border border-line rounded-card p-5 flex flex-col overflow-hidden">
          <div className="flex divide-x divide-line border border-line rounded-lg py-2.5 mb-4 bg-panel-2 shrink-0">
            <Stat value={rows.length} label="RDV du jour" />
            <Stat value={pendingConfirm} label="À confirmer" tone={pendingConfirm ? 'text-st-warn' : 'text-st-ok'} />
            <Stat value={countBy('confirmed')} label="Confirmés" tone="text-st-cy" />
            <Stat value={countBy('held')} label="Honorés" tone="text-st-ok" />
            <Stat value={countBy('no_show')} label="No-show" tone={countBy('no_show') ? 'text-st-bad' : ''} />
          </div>
          <div className="flex-1 overflow-y-auto -mx-1 px-1">
            {loading ? <SkeletonRows count={6} /> : rows.length === 0 ? (
              <EmptyState title="Aucun RDV ce jour" hint="Change de date avec les flèches ci-dessus." />
            ) : (
              <div className="divide-y divide-line">
                {shownRows.map(rdv => (
                  <button key={rdv.id} onClick={() => setSelId(rdv.id)}
                    className={`w-full text-left py-3 flex items-center gap-3 flex-wrap hover:bg-panel-2 transition rounded-lg px-2 -mx-2 ${selId === rdv.id ? 'bg-panel-2 ring-1 ring-line-2' : ''}`}>
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-semibold truncate">
                        {rdv.time?.slice(0, 5)} · {rdv.prospect_name || 'Prospect'}
                        {rdv.company_name && <span className="text-dim font-normal"> · {rdv.company_name}</span>}
                      </div>
                      <div className="font-mono text-[10px] text-faint">{rdv.clients?.name || '—'} · SDR {rdv.sdr?.sales_name || '—'}</div>
                    </div>
                    {isPending(rdv) && isPast(rdv) && <span title="Heure passée — à statuer" className="text-st-warn text-[13px]">⏰</span>}
                    <ContractBadge contractType={contractTypeOf(rdv)} />
                    <StatusBadge status={rdv.lifecycle_status ?? null} />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="bg-panel border border-line rounded-card p-5 overflow-y-auto">
          {sel ? <RdvDetail rdv={sel} role={role} onChanged={load} />
            : <div className="h-full grid place-items-center"><EmptyState title="Sélectionne un RDV" hint="Clique une ligne pour la fiche, confirmer, appeler ou reporter." /></div>}
        </div>
      </div>
    </div>
  );
};

export default RdvConfirmerPage;
